import React, { useState } from 'react';
import {
  X,
  Palette,
  FileText,
  Share2,
  Upload
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const AUDIENCE_OPTIONS = [
  { value: 'bank', label: 'Bank / Lender', emoji: '🏦' },
  { value: 'investor', label: 'Investor', emoji: '💼' },
  { value: 'donor', label: 'Donor / NGO', emoji: '🤝' },
  { value: 'government', label: 'Government', emoji: '🏛️' },
  { value: 'board', label: 'Board / Internal', emoji: '📊' },
  { value: 'internal', label: 'Internal Planning', emoji: '📋' },
  { value: 'custom', label: 'Custom', emoji: '📄' }
];

const THEME_OPTIONS = [
  { value: 'professional', label: 'Professional', colors: ['#1f2937', '#3b82f6'] },
  { value: 'modern', label: 'Modern', colors: ['#0f172a', '#6366f1'] },
  { value: 'minimal', label: 'Minimal', colors: ['#374151', '#9ca3af'] },
  { value: 'custom', label: 'Custom', colors: null }
];

const TemplateSettingsModal = ({ template, onSave, onClose }) => {
  const [settings, setSettings] = useState({
    name: template.name || '',
    description: template.description || '',
    audience: template.audience || 'custom',
    branding: { ...template.branding },
    options: { ...template.options },
    sharing: { ...template.sharing }
  });

  const handleSave = () => {
    onSave(settings);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Template Settings</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="basic" className="mt-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic">Basic</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="document">Document</TabsTrigger>
            <TabsTrigger value="sharing">Sharing</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={settings.name}
                onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                placeholder="Enter template name"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={settings.description}
                onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                placeholder="Brief description of this template"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Target Audience</Label>
              <Select
                value={settings.audience}
                onValueChange={(value) => setSettings({ ...settings, audience: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <span className="flex items-center gap-2">
                        <span>{option.emoji}</span>
                        <span>{option.label}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </TabsContent>

          {/* Branding Tab */}
          <TabsContent value="branding" className="space-y-4 mt-4">
            {/* Logo Upload */}
            <div className="space-y-2">
              <Label>Company Logo</Label>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {settings.branding?.logo_url ? (
                    <div className="relative">
                      <img 
                        src={settings.branding.logo_url} 
                        alt="Logo" 
                        className="w-24 h-24 object-contain border rounded-lg bg-white"
                      />
                      <button
                        onClick={() => setSettings({
                          ...settings,
                          branding: { ...settings.branding, logo_url: '' }
                        })}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                      <Upload className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <Input
                    placeholder="Paste logo URL here..."
                    value={settings.branding?.logo_url || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      branding: { ...settings.branding, logo_url: e.target.value }
                    })}
                  />
                  <p className="text-xs text-gray-500">
                    Enter a URL to your company logo. Recommended size: 200x200px or larger.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) => {
                              setSettings({
                                ...settings,
                                branding: { ...settings.branding, logo_url: ev.target.result }
                              });
                            };
                            reader.readAsDataURL(file);
                          }
                        };
                        input.click();
                      }}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Image
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Theme</Label>
              <div className="grid grid-cols-4 gap-3">
                {THEME_OPTIONS.map((theme) => (
                  <button
                    key={theme.value}
                    onClick={() => setSettings({
                      ...settings,
                      branding: { ...settings.branding, theme: theme.value }
                    })}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      settings.branding?.theme === theme.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {theme.colors && (
                      <div className="flex justify-center gap-1 mb-2">
                        {theme.colors.map((color, i) => (
                          <div
                            key={i}
                            className="w-4 h-4 rounded"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    )}
                    <span className="text-sm">{theme.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Logo Position</Label>
              <Select
                value={settings.branding?.logo_position || 'center'}
                onValueChange={(value) => setSettings({
                  ...settings,
                  branding: { ...settings.branding, logo_position: value }
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {settings.branding?.theme === 'custom' && (
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.branding?.primary_color || '#1f2937'}
                      onChange={(e) => setSettings({
                        ...settings,
                        branding: { ...settings.branding, primary_color: e.target.value }
                      })}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.branding?.primary_color || '#1f2937'}
                      onChange={(e) => setSettings({
                        ...settings,
                        branding: { ...settings.branding, primary_color: e.target.value }
                      })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Secondary Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.branding?.secondary_color || '#3b82f6'}
                      onChange={(e) => setSettings({
                        ...settings,
                        branding: { ...settings.branding, secondary_color: e.target.value }
                      })}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.branding?.secondary_color || '#3b82f6'}
                      onChange={(e) => setSettings({
                        ...settings,
                        branding: { ...settings.branding, secondary_color: e.target.value }
                      })}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Accent Color</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings.branding?.accent_color || '#10b981'}
                      onChange={(e) => setSettings({
                        ...settings,
                        branding: { ...settings.branding, accent_color: e.target.value }
                      })}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.branding?.accent_color || '#10b981'}
                      onChange={(e) => setSettings({
                        ...settings,
                        branding: { ...settings.branding, accent_color: e.target.value }
                      })}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Document Options Tab */}
          <TabsContent value="document" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Page Size</Label>
                <Select
                  value={settings.options?.page_size || 'A4'}
                  onValueChange={(value) => setSettings({
                    ...settings,
                    options: { ...settings.options, page_size: value }
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A4">A4</SelectItem>
                    <SelectItem value="letter">Letter</SelectItem>
                    <SelectItem value="legal">Legal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Orientation</Label>
                <Select
                  value={settings.options?.orientation || 'portrait'}
                  onValueChange={(value) => setSettings({
                    ...settings,
                    options: { ...settings.options, orientation: value }
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait</SelectItem>
                    <SelectItem value="landscape">Landscape</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={settings.options?.include_page_numbers !== false}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    options: { ...settings.options, include_page_numbers: checked }
                  })}
                />
                <Label>Include page numbers</Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  checked={settings.options?.include_header}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    options: { ...settings.options, include_header: checked }
                  })}
                />
                <Label>Include header</Label>
              </div>

              {settings.options?.include_header && (
                <div className="ml-6">
                  <Input
                    value={settings.options?.header_text || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      options: { ...settings.options, header_text: e.target.value }
                    })}
                    placeholder="Header text (use {{company_name}} for dynamic)"
                  />
                </div>
              )}

              <div className="flex items-center gap-2">
                <Checkbox
                  checked={settings.options?.include_footer}
                  onCheckedChange={(checked) => setSettings({
                    ...settings,
                    options: { ...settings.options, include_footer: checked }
                  })}
                />
                <Label>Include footer</Label>
              </div>

              {settings.options?.include_footer && (
                <div className="ml-6">
                  <Input
                    value={settings.options?.footer_text || ''}
                    onChange={(e) => setSettings({
                      ...settings,
                      options: { ...settings.options, footer_text: e.target.value }
                    })}
                    placeholder="Footer text (e.g., Confidential)"
                  />
                </div>
              )}
            </div>
          </TabsContent>

          {/* Sharing Tab */}
          <TabsContent value="sharing" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Visibility</Label>
              <Select
                value={settings.sharing?.visibility || 'private'}
                onValueChange={(value) => setSettings({
                  ...settings,
                  sharing: { ...settings.sharing, visibility: value }
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private (only me)</SelectItem>
                  <SelectItem value="team">Team (share with organization)</SelectItem>
                  <SelectItem value="public">Public (available in gallery)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {settings.sharing?.visibility === 'private' && 'Only you can view and use this template.'}
                {settings.sharing?.visibility === 'team' && 'Team members in your organization can view and use this template.'}
                {settings.sharing?.visibility === 'public' && 'This template will be available in the public template gallery.'}
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save Settings</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSettingsModal;
